import React, { useState, useEffect } from "react";
import { Modal, Input, Form, InputNumber, DatePicker, Button, Card, Tag } from "antd";
import moment from "moment";
import { observer } from "mobx-react";
import { FrTypes } from "../../../common/Fr";
import { Utils } from "../../../common/Utils";
import { Store } from "../../../store/Store";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY/MM/DD";

export const FundraisingDlg = observer(() => {
  const [form] = Form.useForm();
  const { fundEdit } = Store.fundsStore;

  const opt: any = {};
  const [prjType, setPrjType] = useState(0);
  const [orgId, setOrgId] = useState(opt.orgId);

  useEffect(() => {
    setPrjType(fundEdit.fund?.type || 0);
    form.setFieldsValue({ ...fundEdit.fund, range: [moment(fundEdit.fund?.start), moment(fundEdit.fund?.end)] });
  }, [fundEdit.fund]);

  const save = () => {
    form.validateFields().then((values) => {
      fundEdit.save(values);
    });
  };

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  const onValuesChange = (values) => {
    if (Utils.hasKey(values, "type")) {
      setPrjType(values.type);
    }
    if (Utils.hasKey(values, "orgId")) {
      setOrgId(values.orgId);
      form.setFieldsValue({ funOrgId: null });
    }
  };

  return (
    <Modal
      title="Edit Fundraiser"
      visible={fundEdit.visible}
      maskClosable={false}
      onOk={save}
      confirmLoading={fundEdit.saving}
      onCancel={() => fundEdit.close()}
      width={760}
      getContainer={false}
    >
      <Card loading={fundEdit.loading}>
        <Form form={form} {...layout} onValuesChange={onValuesChange}>
          <Form.Item label="Name of Fundraiser" name="name">
            <span className="ant-form-text">{fundEdit.fund?.name}</span>
            {fundEdit.fund?.shareCode && <Tag>{fundEdit.fund?.shareCode}</Tag>}
          </Form.Item>

          <Form.Item label="Type of Fundraiser" name="type">
            <span className="ant-form-text">{FrTypes[fundEdit.fund?.type || 0]}</span>
          </Form.Item>

          <Form.Item label="Status">
            <div>
              <span className="ant-form-text">{fundEdit.fund?.status}</span>
              {Store.isAdmin && fundEdit.fund?.status === "Pending Verification" && (
                <Button.Group>
                  <Button type="primary" onClick={() => fundEdit.accept()}>
                    Accept
                  </Button>
                  <Button danger onClick={() => fundEdit.reject()}>
                    Decline
                  </Button>
                </Button.Group>
              )}
            </div>
          </Form.Item>

          <Form.Item label="Affiliated Organization">
            <span className="ant-form-text">{fundEdit.fund?.org?.name}</span>
          </Form.Item>
          <Form.Item label="Fundraiser Organizer">
            <span className="ant-form-text">{fundEdit.fund?.funOrg?.name}</span>
          </Form.Item>

          <Form.Item
            label="Total Fundraiser Target"
            name="target"
            rules={[{ required: true, message: "Please input target" }]}
          >
            <InputNumber
              min={1}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value: any) => (value || "").replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>
          <Form.Item
            label="Time Duration"
            name="range"
            rules={[{ required: true, message: "Please input Time Duration" }]}
          >
            <RangePicker format={dateFormat} />
          </Form.Item>

          {prjType === 1 && (
            <>
              <Form.Item
                label="Description of Goals"
                name="description"
                rules={[{ required: true, message: "Please inpute description" }]}
              >
                <Input.TextArea
                  maxLength={500}
                  rows={4}
                  placeholder="Eg. We ask each student to perform at least 2 
              community goals they define etc"
                />
              </Form.Item>
              <Form.Item
                label={`Number of ${["", "Goals", "Activity"][prjType]}`}
                name="count"
                rules={[{ required: true, message: "Please input number" }]}
              >
                <InputNumber min={0} />
              </Form.Item>
            </>
          )}
          {prjType === 2 && (
            <>
              <Form.Item label={`Minimum donation / donator`} name={["perGoal", "minimum"]}>
                <InputNumber min={0} placeholder="Optional. Kick off with at least $10.00." />
              </Form.Item>

              <Form.Item
                label={
                  <div>
                    Describe Donation for <br />
                    each Goal Achieved
                  </div>
                }
                name={["perGoal", "description"]}
                rules={[{ required: true, message: "Please input" }]}
              >
                <Input.TextArea
                  style={{ minHeight: 60 }}
                  maxLength={240}
                  placeholder="Example: Donation per Hit, Donation per Lap, Donation / Home Run"
                />
              </Form.Item>

              <Form.Item
                label="Describe the goal in singular"
                name={["perGoal", "singular"]}
                rules={[{ required: true, message: "Please input singular" }]}
              >
                <Input maxLength={60} placeholder="example: run, lap, book" />
              </Form.Item>

              <Form.Item
                label="Describe the goal in plural"
                name={["perGoal", "plural"]}
                rules={[{ required: true, message: "Please input plural" }]}
              >
                <Input maxLength={60} placeholder="example: runs, laps, books" />
              </Form.Item>
            </>
          )}

          <Form.Item
            label={prjType === 2 ? "Describe Fundraising & Purpose" : "Use of Proceeds"}
            name="proceeds"
            rules={[{ required: true, message: "Please inpute description" }]}
          >
            <Input.TextArea
              maxLength={500}
              rows={6}
              placeholder="This language will be displayed on the fundraiser dashboard page for parents. Useful background on the primary uses of the funds will help parents make their donation decision."
            />
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
});
